import { useQuery } from '@tanstack/react-query';
import type { Aplicacion, Corte, Resultados, TextosResultado } from '@copathy/shared';
import { api } from './api';

/**
 * M8 del lado de la pantalla.
 *
 * ── El listado va por `/api/resultados`, no por `/api/aplicaciones` ──
 * **`aplicaciones.ver` lo tienen solo los roles de COPATHY.** La empresa cliente
 * consulta sus resultados pero no administra aplicaciones, así que pedir
 * `/api/aplicaciones` desde la pantalla de resultados le daría 403 al cliente —
 * y sin lista no hay pantalla.
 */

/** Las aplicaciones con informe disponible. RLS decide el alcance. */
export function useAplicacionesConResultados() {
  return useQuery({
    queryKey: ['resultados', 'aplicaciones'],
    queryFn: () => api.get<Aplicacion[]>('/api/resultados/aplicaciones'),
  });
}

/**
 * El informe de una aplicación, con el corte pedido.
 *
 * ⚠️ El corte viaja en la `queryKey`: sin él, TanStack Query devolvería el
 * informe del área anterior al cambiar de selección y la pantalla mostraría
 * datos de otro grupo con el nombre del nuevo. Es el error más caro posible en
 * esta pantalla — quien mira no tiene forma de notarlo.
 */
export function useResultados(
  aplicacionId: number | null,
  corte: { area?: string; cargo?: string } = {},
) {
  const parametros = new URLSearchParams();
  if (corte.area) parametros.set('area', corte.area);
  if (corte.cargo) parametros.set('cargo', corte.cargo);
  const cadena = parametros.toString();

  return useQuery({
    queryKey: ['resultados', aplicacionId, corte.area ?? '', corte.cargo ?? ''],
    queryFn: () =>
      api.get<Resultados>(
        `/api/aplicaciones/${aplicacionId}/resultados${cadena ? `?${cadena}` : ''}`,
      ),
    enabled: aplicacionId !== null,
  });
}

/** Las áreas y cargos con respuestas suficientes, para armar el selector. */
export function useCortes(aplicacionId: number | null) {
  return useQuery({
    queryKey: ['resultados', 'cortes', aplicacionId],
    queryFn: () => api.get<Corte[]>(`/api/aplicaciones/${aplicacionId}/resultados/cortes`),
    enabled: aplicacionId !== null,
  });
}

/**
 * Las respuestas de texto libre de una pregunta.
 *
 * ⚠️ `enabled` arranca en `false` a propósito: **no se piden hasta que alguien
 * las pide.** Traerlas con el informe las metería en la caché del navegador de
 * cualquiera que abra la pantalla, y un texto sobre el propio jefe es
 * identificable por su contenido en un equipo pequeño (D-114). Que haga falta un
 * clic no es fricción: es que el dato más delicado no viaje solo.
 */
export function useTextos(aplicacionId: number | null, preguntaId: number | null, activo: boolean) {
  return useQuery({
    queryKey: ['resultados', 'textos', aplicacionId, preguntaId],
    queryFn: () =>
      api.get<TextosResultado>(
        `/api/aplicaciones/${aplicacionId}/resultados/textos/${preguntaId}`,
      ),
    enabled: activo && aplicacionId !== null && preguntaId !== null,
  });
}
